export class ReportBuilder {
    constructor() {
        this.root = null;
        this.previewChart = null;
        this.previewTimer = null;
        this.state = {
            currentStep: 'data',
            report: null,
            defaults: {
                reportTypes: ['chart', 'table', 'list'],
                chartTypes: ['bar', 'line', 'pie', 'doughnut'],
                aggregateTypes: ['count', 'sum', 'avg']
            }
        };
    }

    initialize(root = document) {
        const builderRoot = root.matches?.('[data-report-builder-root]')
            ? root
            : root.querySelector?.('[data-report-builder-root]');
        if (!builderRoot) {
            return;
        }

        this.root = builderRoot;
        this.cacheElements();
        this.bindStaticEvents();
        this.initializeReport();
    }

    cacheElements() {
        this.editorView = this.root.querySelector('[data-report-editor-view]');
        this.editorTitle = this.root.querySelector('[data-report-editor-title]');
        this.status = this.root.querySelector('[data-report-status]');
        this.runLink = this.root.querySelector('[data-report-run-link]');
        this.previewTitle = this.root.querySelector('[data-report-preview-title]');
        this.previewSubtitle = this.root.querySelector('[data-report-preview-subtitle]');
        this.previewType = this.root.querySelector('[data-report-preview-type]');
        this.previewMeta = this.root.querySelector('[data-report-preview-meta]');
        this.previewCanvas = this.root.querySelector('[data-report-preview-canvas]');
        this.previewTable = this.root.querySelector('[data-report-preview-table]');
        this.previewVisual = this.root.querySelector('[data-report-preview-visual]');
        this.previewEmpty = this.root.querySelector('[data-report-preview-empty]');
        this.previewError = this.root.querySelector('[data-report-preview-error]');
        this.stepButtons = this.root.querySelectorAll('[data-report-step]');
        this.stepPanels = this.root.querySelectorAll('[data-report-step-panel]');
        this.chartOnlyFields = this.root.querySelectorAll('[data-report-chart-field]');
        this.tableOnlyFields = this.root.querySelectorAll('[data-report-table-field]');
        this.fields = {
            title: this.root.querySelector('[data-report-field="title"]'),
            report_type: this.root.querySelector('[data-report-field="report_type"]'),
            chart_type: this.root.querySelector('[data-report-field="chart_type"]'),
            table_name: this.root.querySelector('[data-report-field="table_name"]'),
            group_by_field: this.root.querySelector('[data-report-field="group_by_field"]'),
            aggregate_type: this.root.querySelector('[data-report-field="aggregate_type"]'),
            aggregate_field: this.root.querySelector('[data-report-field="aggregate_field"]'),
            columns: this.root.querySelector('[data-report-field="columns"]'),
            row_limit: this.root.querySelector('[data-report-field="row_limit"]'),
            filter_query: this.root.querySelector('[data-report-field="filter_query"]'),
            drilldown_enabled: this.root.querySelector('[data-report-field="drilldown_enabled"]'),
            options_json: this.root.querySelector('[data-report-field="options_json"]'),
            is_active: this.root.querySelector('[data-report-field="is_active"]')
        };
    }

    bindStaticEvents() {
        if (this.bound) {
            return;
        }
        this.bound = true;

        this.root.querySelector('[data-report-save]')?.addEventListener('click', () => this.saveReport());
        this.root.querySelector('[data-report-refresh]')?.addEventListener('click', () => this.previewReport());
        this.root.querySelector('[data-report-run]')?.addEventListener('click', () => this.previewReport());

        this.stepButtons.forEach((button) => {
            button.addEventListener('click', () => this.showStep(button.dataset.reportStep));
        });
        this.root.querySelectorAll('[data-report-step-next]').forEach((button) => {
            button.addEventListener('click', () => this.showStep(button.dataset.reportStepNext));
        });

        Object.values(this.fields).forEach((field) => {
            if (!field) {
                return;
            }
            const eventName = field.type === 'checkbox' || field.tagName === 'SELECT' ? 'change' : 'input';
            field.addEventListener(eventName, () => {
                this.syncReportState();
                this.updateFieldVisibility();
                this.schedulePreview();
                if (field === this.fields.title && this.editorTitle) {
                    this.editorTitle.textContent = this.fields.title.value.trim() || 'Report';
                }
            });
        });
    }

    async initializeReport() {
        try {
            const response = await fetch('/api/reports/builder', { credentials: 'same-origin' });
            const payload = await response.json();
            if (!response.ok) {
                throw new Error(payload.error || 'Unable to load report defaults');
            }
            this.state.defaults = payload.defaults || this.state.defaults;
            this.populateSelects();
        } catch (error) {
            console.error('Report defaults load failed', error);
            this.setStatus(error.message || 'Unable to load report defaults');
        }

        const reportId = (this.root?.dataset?.reportId || '').trim();
        if (reportId) {
            await this.loadReport(reportId);
        } else {
            await this.createNewReport();
        }
    }

    populateSelects() {
        this.fields.report_type.innerHTML = this.state.defaults.reportTypes
            .map((reportType) => `<option value="${reportType}">${this.humanize(reportType)}</option>`)
            .join('');
        this.fields.chart_type.innerHTML = this.state.defaults.chartTypes
            .map((chartType) => `<option value="${chartType}">${this.humanize(chartType)}</option>`)
            .join('');
        this.fields.aggregate_type.innerHTML = this.state.defaults.aggregateTypes
            .map((aggregateType) => `<option value="${aggregateType}">${this.humanize(aggregateType)}</option>`)
            .join('');
    }

    showStep(name) {
        if (!name) {
            return;
        }
        this.state.currentStep = name;
        this.stepButtons.forEach((button) => {
            button.classList.toggle('is-active', button.dataset.reportStep === name);
        });
        this.stepPanels.forEach((panel) => {
            panel.classList.toggle('hidden', panel.dataset.reportStepPanel !== name);
        });
    }

    showEditor() {
        this.showStep(this.state.currentStep || 'data');
        if (this.editorTitle) {
            this.editorTitle.textContent = this.state.report?.title?.trim() || 'Report';
        }
    }

    async createNewReport() {
        const response = await fetch('/api/reports/builder/template', { credentials: 'same-origin' });
        const payload = await response.json();
        this.state.report = payload.report;
        this.fillForm();
        this.refreshRunLink();
        this.setStatus('Draft report ready. Configure it and watch the view run preview update.');
        this.showEditor();
        this.previewReport();
    }

    async loadReport(reportId) {
        this.setStatus('Loading report...');
        const response = await fetch(`/api/reports/${reportId}/builder`, { credentials: 'same-origin' });
        const payload = await response.json();
        if (!response.ok) {
            this.setStatus(payload.error || 'Unable to load report');
            return;
        }
        this.state.report = payload.report;
        this.fillForm();
        this.refreshRunLink();
        this.setStatus(`Loaded ${this.state.report.title || 'report'}.`);
        this.showEditor();
        this.previewReport();
    }

    fillForm() {
        const report = this.state.report || {};
        this.fields.title.value = report.title || '';
        this.fields.report_type.value = report.report_type || this.state.defaults.reportTypes[0];
        this.fields.chart_type.value = report.chart_type || this.state.defaults.chartTypes[0];
        this.fields.table_name.value = report.table_name || '';
        this.fields.group_by_field.value = report.group_by_field || '';
        this.fields.aggregate_type.value = report.aggregate_type || this.state.defaults.aggregateTypes[0];
        this.fields.aggregate_field.value = report.aggregate_field || '';
        this.fields.columns.value = report.columns || '';
        this.fields.row_limit.value = report.row_limit || 25;
        this.fields.filter_query.value = report.filter_query || '';
        this.fields.drilldown_enabled.checked = report.drilldown_enabled !== false;
        this.fields.options_json.value = report.options_json || '{}';
        this.fields.is_active.checked = Boolean(report.is_active);
        this.updateFieldVisibility();
    }

    syncReportState() {
        if (!this.state.report) {
            return;
        }
        this.state.report = this.collectForm();
        this.refreshRunLink();
    }

    collectForm() {
        return {
            sys_id: this.state.report?.sys_id || crypto.randomUUID(),
            title: this.fields.title.value.trim(),
            report_type: this.fields.report_type.value,
            chart_type: this.fields.chart_type.value,
            table_name: this.fields.table_name.value.trim(),
            group_by_field: this.fields.group_by_field.value.trim(),
            aggregate_type: this.fields.aggregate_type.value,
            aggregate_field: this.fields.aggregate_field.value.trim(),
            columns: this.fields.columns.value.trim(),
            row_limit: this.fields.row_limit.value.trim(),
            filter_query: this.fields.filter_query.value.trim(),
            drilldown_enabled: this.fields.drilldown_enabled.checked,
            options_json: this.fields.options_json.value.trim() || '{}',
            is_active: this.fields.is_active.checked,
            persisted: Boolean(this.state.report?.persisted)
        };
    }

    async saveReport() {
        const report = this.collectForm();
        try {
            const response = await fetch('/api/reports/builder', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(report)
            });
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.error || 'Unable to save report');
            }
            this.state.report = { ...(result.report || report), persisted: true };
            this.fillForm();
            this.refreshRunLink();
            if (this.editorTitle) {
                this.editorTitle.textContent = this.state.report.title?.trim() || 'Report';
            }
            this.setStatus(`Saved ${this.state.report.title}. You can run it now and attach chart reports to dashboards.`);
            this.previewReport();
        } catch (error) {
            console.error('Save report failed', error);
            this.setStatus(error.message || 'Unable to save report');
        }
    }

    schedulePreview() {
        window.clearTimeout(this.previewTimer);
        this.previewTimer = window.setTimeout(() => this.previewReport(), 350);
    }

    async previewReport() {
        const report = this.collectForm();
        const isChart = report.report_type === 'chart';
        this.previewTitle.textContent = report.title || 'Draft Report';
        this.previewSubtitle.textContent = isChart && report.table_name && report.group_by_field
            ? `${report.aggregate_type} grouped by ${report.group_by_field} from ${report.table_name}`
            : !isChart && report.table_name
                ? `${report.report_type} report from ${report.table_name}`
            : 'Configure the report to see live output.';
        this.previewType.textContent = isChart ? (report.chart_type || 'bar') : (report.report_type || 'table');

        if (!this.canPreviewReport(report)) {
            this.setPreviewMeta(isChart
                ? 'Add a title, source table, and group by field to preview this report.'
                : 'Add a title, source table, and at least one column to preview this report.');
            this.togglePreviewState({ empty: true, error: false });
            this.destroyPreviewChart();
            this.renderPreviewTable(null);
            return;
        }

        this.setPreviewMeta('Running report...');
        this.togglePreviewState({ empty: false, error: false });

        try {
            const response = await fetch('/api/reports/preview', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(report)
            });
            const payload = await response.json();
            if (!response.ok) {
                throw new Error(payload.error || 'Unable to preview report');
            }

            if (payload.reportType === 'chart') {
                const labels = Array.isArray(payload?.data?.labels) ? payload.data.labels : [];
                const datasets = Array.isArray(payload?.data?.datasets) ? payload.data.datasets : [];
                if (!labels.length || !datasets.length) {
                    this.destroyPreviewChart();
                    this.renderPreviewTable(null);
                    this.togglePreviewState({ empty: true, error: false });
                    this.setPreviewMeta(payload?.meta?.summaryText || 'No data returned for this report');
                    return;
                }
                this.renderPreviewChart(payload);
            } else {
                const rows = Array.isArray(payload?.data?.rows) ? payload.data.rows : [];
                if (!rows.length) {
                    this.destroyPreviewChart();
                    this.renderPreviewTable(payload);
                    this.togglePreviewState({ empty: true, error: false });
                    this.setPreviewMeta(payload?.meta?.summaryText || 'No data returned for this report');
                    return;
                }
                this.destroyPreviewChart();
                this.renderPreviewTable(payload);
            }
            this.togglePreviewState({ empty: false, error: false });
            this.setPreviewMeta(payload?.meta?.summaryText || 'Updated');
        } catch (error) {
            console.error('Preview report failed', error);
            this.destroyPreviewChart();
            this.renderPreviewTable(null);
            this.togglePreviewState({ empty: false, error: true, errorText: error.message || 'Unable to preview report' });
            this.setPreviewMeta('Preview failed');
        }
    }

    renderPreviewChart(payload) {
        if (!this.previewCanvas || !window.Chart) {
            return;
        }
        this.showPreviewMode('chart');
        this.destroyPreviewChart();
        const chartType = payload.chartType || this.fields.chart_type.value || 'bar';
        const drilldownUrls = Array.isArray(payload?.meta?.drilldownUrls) ? payload.meta.drilldownUrls : [];
        this.previewChart = new window.Chart(this.previewCanvas, {
            type: chartType,
            data: payload.data,
            options: this.deepMerge(this.buildOptions(chartType, payload.options || {}), {
                onClick: (_event, elements) => {
                    const index = elements?.[0]?.index;
                    const targetUrl = Number.isInteger(index) ? drilldownUrls[index] : '';
                    if (targetUrl) {
                        window.location.href = targetUrl;
                    }
                }
            })
        });
        this.previewCanvas.style.cursor = drilldownUrls.length ? 'pointer' : 'default';
    }

    renderPreviewTable(payload) {
        if (!this.previewTable) {
            return;
        }
        if (!payload || !Array.isArray(payload?.data?.columns) || !Array.isArray(payload?.data?.rows)) {
            this.previewTable.innerHTML = '';
            return;
        }
        this.showPreviewMode('table');
        const columns = payload.data.columns;
        const rows = payload.data.rows;
        this.previewTable.innerHTML = `
            <div class="overflow-x-auto">
                <table class="table table-pin-rows table-sm md:table-md">
                    <thead>
                        <tr>${columns.map((column) => `<th>${this.escape(this.humanize(column))}</th>`).join('')}</tr>
                    </thead>
                    <tbody>
                        ${rows.map((row) => `
                            <tr>
                                ${columns.map((column, index) => {
                                    const value = this.escape(row[column] || '');
                                    const recordUrl = row._recordUrl || '#';
                                    return index === 0 && row._recordUrl
                                        ? `<td><a href="${recordUrl}" class="link link-primary link-hover">${value}</a></td>`
                                        : `<td>${value}</td>`;
                                }).join('')}
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        `;
    }

    buildOptions(chartType, payloadOptions) {
        const defaults = {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 300, easing: 'easeOutQuart' },
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                    labels: { boxWidth: 12, usePointStyle: true, pointStyle: 'circle' }
                }
            },
            scales: chartType === 'pie' || chartType === 'doughnut'
                ? {}
                : {
                    x: { grid: { display: false }, ticks: { color: '#64748b' } },
                    y: { beginAtZero: true, grid: { color: 'rgba(148, 163, 184, 0.18)' }, ticks: { color: '#64748b' } }
                }
        };
        return this.deepMerge(defaults, payloadOptions);
    }

    destroyPreviewChart() {
        if (this.previewChart) {
            this.previewChart.destroy();
            this.previewChart = null;
        }
    }

    showPreviewMode(mode) {
        const chartMode = mode === 'chart';
        this.previewCanvas?.classList.toggle('hidden', !chartMode);
        this.previewTable?.classList.toggle('hidden', chartMode);
        if (this.previewVisual) {
            this.previewVisual.classList.toggle('h-auto', !chartMode);
            this.previewVisual.classList.toggle('min-h-[18rem]', !chartMode);
        }
    }

    setPreviewMeta(text) {
        this.previewMeta.innerHTML = '';
        const badge = document.createElement('span');
        badge.className = 'rounded-full bg-base-200 px-3 py-1';
        badge.textContent = text;
        this.previewMeta.appendChild(badge);
    }

    togglePreviewState({ empty = false, error = false, errorText = '' }) {
        this.previewEmpty.classList.toggle('hidden', !empty);
        this.previewEmpty.classList.toggle('flex', empty);
        this.previewError.textContent = errorText;
        this.previewError.classList.toggle('hidden', !error);
        this.previewError.classList.toggle('flex', error);
    }

    refreshRunLink() {
        if (!this.state.report?.persisted) {
            this.runLink.href = '#';
            this.runLink.classList.add('pointer-events-none', 'opacity-50');
            return;
        }
        this.runLink.href = `/nav/report/${this.state.report.sys_id}/run`;
        this.runLink.classList.remove('pointer-events-none', 'opacity-50');
    }

    updateFieldVisibility() {
        const reportType = this.fields.report_type.value || 'chart';
        const chartMode = reportType === 'chart';
        this.chartOnlyFields.forEach((field) => {
            field.classList.toggle('hidden', !chartMode);
        });
        this.tableOnlyFields.forEach((field) => {
            field.classList.toggle('hidden', chartMode);
        });
    }

    canPreviewReport(report) {
        if (!report.title || !report.table_name) {
            return false;
        }
        if (report.report_type === 'chart') {
            return Boolean(report.group_by_field);
        }
        return Boolean(report.columns);
    }

    setStatus(message) {
        if (this.status) {
            this.status.textContent = message;
        }
    }

    humanize(value) {
        return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    }

    escape(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    deepMerge(...sources) {
        const result = {};
        sources.forEach((source) => {
            if (!source || typeof source !== 'object' || Array.isArray(source)) {
                return;
            }
            Object.keys(source).forEach((key) => {
                const existingValue = result[key];
                const nextValue = source[key];
                if (this.isPlainObject(existingValue) && this.isPlainObject(nextValue)) {
                    result[key] = this.deepMerge(existingValue, nextValue);
                } else {
                    result[key] = nextValue;
                }
            });
        });
        return result;
    }

    isPlainObject(value) {
        return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
    }
}
